import {
  endOfMonth,
  format,
  parseISO,
  startOfMonth,
  subMonths,
} from "date-fns";
import { enqueueSnackbar } from "notistack";
import { useEffect, useMemo, useRef } from "react";
import {
  EFilterCalendarType,
  IFilterMonthItem,
} from "src/shared/components/Table/model/types";
import { IBaseQueryParams } from "src/shared/types/query";
import { queryCleanEmpty } from "src/shared/utils/url/query.helpers";
import { useTableSearchParams } from "../useTableSearchParams";

export interface IGetErpPartnerClientsQuery extends IBaseQueryParams {
  partner_id: string;
  start_date: string;
  end_date: string;

  month_from?: string; // "YYYY-MM"
  month_to?: string; // "YYYY-MM"
}

const YM = "yyyy-MM";
const YMD = "yyyy-MM-dd";

const toStartDate = (ym: string) =>
  format(startOfMonth(parseISO(`${ym}-01`)), YMD);

const toEndDate = (ym: string) => format(endOfMonth(parseISO(`${ym}-01`)), YMD);

const safeIsoDate = (v?: string) => {
  if (!v) return null;
  const d = parseISO(v);

  return Number.isNaN(d.getTime()) ? null : d;
};

export function useGetErpPartnerClientsQuery() {
  const { paramsObject, onResetFilter, onFilter } =
    useTableSearchParams<IGetErpPartnerClientsQuery>();

  const { partner_id, start_date, end_date, month_from, month_to } =
    paramsObject;

  const didInitRef = useRef(false);

  const defaultMonth = useMemo(() => format(subMonths(new Date(), 1), YM), []);
  const defaultStart = useMemo(() => toStartDate(defaultMonth), [defaultMonth]);
  const defaultEnd = useMemo(() => toEndDate(defaultMonth), [defaultMonth]);

  const computedStart = useMemo(() => {
    if (month_from) return toStartDate(month_from);

    const s = safeIsoDate(start_date);
    const e = safeIsoDate(end_date);

    if (s && e && e >= s) return format(s, YMD);

    return defaultStart;
  }, [month_from, start_date, end_date, defaultStart]);

  const computedEnd = useMemo(() => {
    if (month_to) return toEndDate(month_to);

    const s = safeIsoDate(start_date);
    const e = safeIsoDate(end_date);

    if (s && e && e >= s) return format(e, YMD);

    return defaultEnd;
  }, [month_to, start_date, end_date, defaultEnd]);

  useEffect(() => {
    const s = safeIsoDate(computedStart);
    const e = safeIsoDate(computedEnd);
    if (!s || !e) return;

    if (e < s) {
      enqueueSnackbar("Дата окончания не может быть раньше даты начала", {
        variant: "error",
      });

      onResetFilter(["month_from", "month_to"]);
    }
  }, [computedStart, computedEnd, onResetFilter]);

  const query = useMemo(
    () =>
      queryCleanEmpty({
        partner_id,
        start_date: computedStart,
        end_date: computedEnd,
      }),
    [partner_id, computedStart, computedEnd],
  );

  const filters: IFilterMonthItem[] = useMemo(
    () => [
      {
        name: "month_from",
        type: EFilterCalendarType.MONTH,
        value: month_from || null,
        title: "От",
      },
      {
        name: "month_to",
        type: EFilterCalendarType.MONTH,
        value: month_to || null,
        title: "До",
      },
    ],
    [month_from, month_to],
  );

  useEffect(() => {
    if (didInitRef.current) return;

    if (month_from || month_to) {
      didInitRef.current = true;

      return;
    }

    onFilter([
      { name: "month_from", value: defaultMonth },
      { name: "month_to", value: defaultMonth },
    ]);

    didInitRef.current = true;
  }, [onFilter, month_from, month_to, defaultMonth]);

  return {
    query,
    filters,
    onResetFilter,
    onFilter,
  };
}
